import { zodResolver } from '@hookform/resolvers/zod';
import { changePasswordSchema, setPasswordSchema } from '@willnq/shared/validation/authSchemas';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';

import {
  GoogleSignInCancelledError,
  useGoogleOAuth,
} from '@/features/identity/hooks/useGoogleOAuth';
import { useToast } from '@/hooks/useToast';
import { authService } from '@/services/authService';
import { clientOrServerMessage, parseRestError } from '@/utils/errorUtils';

type ChangePasswordFormValues = z.infer<typeof changePasswordSchema>;

/**
 * Drives `ChangePasswordForm` in whichever of its two modes the account is in.
 *
 * <p>`hasPassword` is `CurrentUser.hasPassword`, the credential-shape signal the server publishes on
 * `/account/me` (BR-8). It is never inferred from the email address: a `@gmail.com` account may well
 * have a password, and a corporate one may have none.</p>
 *
 * <p>True means confirm the current password. False means the account is setting its first, and
 * re-proves itself with a fresh Google sign-in instead, the same credential the MFA reset accepts from
 * such an account. Without that step a stolen session alone could mint a password outliving the
 * session.</p>
 */
export function useChangePassword(hasPassword: boolean) {
  const { showSuccess } = useToast();
  const [serverError, setServerError] = useState<string | null>(null);
  const { requestAccessToken, isConfigured: googleConfigured } = useGoogleOAuth();

  const form = useForm<ChangePasswordFormValues>({
    // Both schemas apply the same `passwordSchema` to the new password and differ only in whether a
    // current password is required.
    resolver: zodResolver(hasPassword ? changePasswordSchema : setPasswordSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { currentPassword: '', newPassword: '', confirmNewPassword: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      // Obtained before the request, so a cancelled popup sends nothing at all.
      const credential = hasPassword
        ? { currentPassword: values.currentPassword }
        : { oAuthProvider: 'Google' as const, oAuthAccessToken: await requestAccessToken() };

      await authService.changePassword({ ...credential, newPassword: values.newPassword });
      form.reset();
      showSuccess(hasPassword ? 'Your password has been changed.' : 'Your WillnQ password is set.');
    } catch (error) {
      // Dismissing the popup is a choice, not a failure, and nothing was sent.
      if (error instanceof GoogleSignInCancelledError) return;

      const { code } = parseRestError(error);
      const message = clientOrServerMessage(error);

      // Attached to the field only when the field is on screen. `CurrentPasswordRequired` can reach the
      // set-password form when the server holds a password this client did not know about, and there is
      // no input to attach it to there, so it surfaces as the banner instead of vanishing.
      if (
        hasPassword &&
        (code === 'Identity.InvalidCredentials' || code === 'Identity.CurrentPasswordRequired')
      ) {
        form.setError('currentPassword', { message });
      } else {
        setServerError(message);
      }
    }
  });

  return { form, onSubmit, serverError, googleConfigured };
}
